import React, { useEffect, useState } from 'react';
import {
  Table, Button, Drawer, Form, Input, Select, InputNumber, DatePicker, Popconfirm, message, Space, Grid,
} from 'antd';
import axios from 'axios';
import moment from 'moment';

const { Option } = Select;
const { useBreakpoint } = Grid;

const goalTypeOptions = [
  { label: 'Weight Loss', value: 'weight-loss' },
  { label: 'Calorie Deficit', value: 'caloriedeficit' },
];

const goalQuantityTypeOptions = [
  { label: 'Kg', value: 'kg' },
  { label: 'Kcals', value: 'kcals' },
];

const GoalsPage = () => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [form] = Form.useForm();
  const screens = useBreakpoint();

  const fetchGoals = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/goals`, { withCredentials: true });
      setGoals(res.data);
    } catch (e) {
      message.error('Failed to fetch goals');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchGoals();
  }, []);

  const openDrawerForCreate = () => {
    setEditingGoal(null);
    form.resetFields();
    setDrawerVisible(true);
  };

  const openDrawerForEdit = (goal) => {
    setEditingGoal(goal);
    form.setFieldsValue({
      ...goal,
      goalTargetDate: moment(goal.goalTargetDate),
    });
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    form.resetFields();
    setEditingGoal(null);
  };

  const onFinish = async (values) => {
    const payload = {
      goalName: values.goalName,
      goalType: values.goalType,
      goalQuantity: values.goalQuantity,
      goalQuantityType: values.goalQuantityType,
      dailyGoalQuantity: values.dailyGoalQuantity,
      goalTargetDate: values.goalTargetDate.toISOString(),
    };

    try {
      if (editingGoal) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/goals/${editingGoal.id}`, payload, { withCredentials: true });
        message.success('Goal updated');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/goals`, payload, { withCredentials: true });
        message.success('Goal created');
      }
      closeDrawer();
      fetchGoals();
    } catch (e) {
      message.error('Failed to save goal');
    }
  };

  const onDelete = async (goalId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/goals/${goalId}`, { withCredentials: true });
      message.success('Goal deleted');
      fetchGoals();
    } catch {
      message.error('Failed to delete goal');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'goalName',
      key: 'goalName'
    },
    {
      title: 'Type',
      dataIndex: 'goalType',
      key: 'goalType',
      render: (text) => goalTypeOptions.find(o => o.value === text)?.label || text
    },
    {
      title: 'Quantity',
      dataIndex: 'goalQuantity',
      key: 'goalQuantity'
    },
    {
      title: 'Quantity Type',
      dataIndex: 'goalQuantityType',
      key: 'goalQuantityType',
      render: (text) => goalQuantityTypeOptions.find(o => o.value === text)?.label || text
    },
    {
      title: 'Daily Quantity',
      dataIndex: 'dailyGoalQuantity',
      key: 'dailyGoalQuantity'
    },
    {
      title: 'Target Date',
      dataIndex: 'goalTargetDate',
      key: 'goalTargetDate',
      render: date => moment(date).format('YYYY-MM-DD')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openDrawerForEdit(record)}>Edit</Button>
          <Popconfirm
            title="Are you sure to delete this goal?"
            onConfirm={() => onDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: screens.xs ? 12 : 24}}>
      <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
      <Button type="primary" onClick={openDrawerForCreate} style={{ marginBottom: 16 }}>
        Add Goal
      </Button>
      </Space>
   
   <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <Table
        dataSource={goals}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 'max-content' }}
        bordered
        size="middle"
      />
      </div>
      
      <Drawer
        title={editingGoal ? 'Edit Goal' : 'Add Goal'}
        width={screens.sm ? 480 : '100%'}
        onClose={closeDrawer}
        open={drawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            goalType: 'weight-loss',
            goalQuantityType: 'kg',
          }}
        >
          <Form.Item
            name="goalName"
            label="Goal Name"
            rules={[{ required: true, message: 'Please enter the goal name' }]}
          >
            <Input placeholder="Enter goal name" />
          </Form.Item>

          <Form.Item
            name="goalType"
            label="Goal Type"
            rules={[{ required: true, message: 'Please select the goal type' }]}
          >
            <Select options={goalTypeOptions} />
          </Form.Item>

          <Form.Item
            name="goalQuantity"
            label="Goal Quantity"
            rules={[{ required: true, message: 'Please enter the goal quantity' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="goalQuantityType"
            label="Goal Quantity Type"
            rules={[{ required: true, message: 'Please select the quantity type' }]}
          >
            <Select options={goalQuantityTypeOptions} />
          </Form.Item>

          <Form.Item
            name="dailyGoalQuantity"
            label="Daily Goal Quantity"
            rules={[{ required: true, message: 'Please enter the daily goal quantity' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="goalTargetDate"
            label="Goal Target Date"
            rules={[{ required: true, message: 'Please select the target date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button onClick={closeDrawer}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {editingGoal ? 'Update Goal' : 'Add Goal'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default GoalsPage;
